import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Tabs,
  Tab,
  Form,
  Alert,
  Container,
  Row,
  Badge,
} from "react-bootstrap";
import { FaSave } from "react-icons/fa";
import { getHistoryNotes, createAccountNotes, updateAccountNotes } from "../../api";
import { jwtDecode } from "jwt-decode";
import "../Services/Services.css";

// NotesHistoryModal now uses onHide prop for closing
const NotesHistoryModal = ({ show, onHide, accountId }) => {
  const [activeTab, setActiveTab] = useState("notes");
  const [user, setUser] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [displayOnStatement, setDisplayOnStatement] = useState(false);
  const [notes, setNotes] = useState([]);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);

  // Load notes and user when modal opens
  useEffect(() => {
    if (show && accountId) {
      loadNotes();
      const token = sessionStorage.getItem("user");
      if (token) {
        const decoded = jwtDecode(token);
        setUser(decoded);
      }
    }
  }, [show, accountId]);

  const loadNotes = async () => {
    try {
      const data = await getHistoryNotes(accountId);
      // Separate into notes and history based on type
      const notesArray = data
        .filter((item) => item.type === "Notes")
        .map((item) => ({ ...item, id: item._id || item.id }))
        .sort((a, b) => new Date(b.dateCreated) - new Date(a.dateCreated));
      const historyArray = data
        .filter((item) => item.type === "History")
        .map((item) => ({ ...item, id: item._id || item.id }))
        .sort((a, b) => new Date(b.dateCreated) - new Date(a.dateCreated));

      setNotes(notesArray);
      setHistory(historyArray);
    } catch (err) {
      console.error(err);
      setError("Failed to load notes and history.");
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    const userName = user
      ? `${user.firstName} ${user.lastName}`
      : "Unknown User";
    const payload = {
      accountId,
      type: "Notes",
      note: noteText.trim(),
      displayOnStatement,
      userId: user ? user._id : null,
      userFirstName: user ? user.firstName : "Unknown",
      userLastName: user ? user.lastName : "User",
      dateCreated: new Date().toISOString(),
    };
    try {
      const newNote = await createAccountNotes(payload);
      const createdDate = newNote.dateCreated || payload.dateCreated;
      setNotes((prev) => [
        {
          ...newNote,
          id: newNote._id || newNote.id,
          note: noteText.trim(),
          displayOnStatement: displayOnStatement,
          userName,
          dateCreated: createdDate,
        },
        ...prev,
      ]);
      setNoteText("");
      setDisplayOnStatement(false);
    } catch (err) {
      console.error(err);
      setError("Could not add note.");
    }
  };

  const handleToggleDisplayFlag = async (note) => {
    try {
      // Use _id for the API call (MongoDB ID)
      const noteId = note._id || note.id;
      
      // Update in database
      await updateAccountNotes(noteId, { displayOnStatement: !note.displayOnStatement });
      
      // Update local state
      setNotes((prev) =>
        prev.map((n) =>
          n.id === note.id
            ? { ...n, displayOnStatement: !note.displayOnStatement }
            : n
        )
      );
    } catch (err) {
      console.error(err);
      setError("Could not update display flag.");
    }
  };

  const formatDate = (isoString) => {
    const d = new Date(isoString);
    const pad = (n) => n.toString().padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
      d.getDate()
    )} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  return (
    <Modal
      show={show}
      size="lg"
      dialogClassName="modal-80vh"
      aria-labelledby="notes-history-modal"
      centered
      onHide={onHide}
    >
      <Modal.Header closeButton className="border-secondary">
        <Modal.Title id="notes-history-modal" className="text-primary">
          Account Notes
        </Modal.Title>
      </Modal.Header>

      <Modal.Body
        style={{
          height: "80vh",
          display: "flex",
          flexDirection: "column",
          padding: 0,
        }}
      >
        <Container className="px-4 py-3 flex-shrink-0">
          {/* Tabs with full-width bottom border */}
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            id="notes-history-tabs"
            className="w-100 custom-tabs"
          >
            <Tab eventKey="notes" title="Notes" />
            <Tab eventKey="history" title="History" />
          </Tabs>

          {/* Tab Content */}
          {activeTab === "notes" && (
            <div className="border-top border-1 border-secondary mb-0">
              <Form.Group controlId="noteText" className="mb-2">
                <Form.Label className="fw-bold mt-2">Add Note</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Type your note here..."
                />
              </Form.Group>
              <Form.Group controlId="displayOnStatement" className="mb-2">
                <Form.Check
                  type="checkbox"
                  label="Display on Statement"
                  checked={displayOnStatement}
                  onChange={(e) => setDisplayOnStatement(e.target.checked)}
                />
              </Form.Group>
              <Button
                variant="primary"
                onClick={handleAddNote}
                className="mb-4"
              >
                <FaSave className="me-1" /> Save Note
              </Button>
              <br />
              <Form.Label className="fw-bold">Previous Notes:</Form.Label>
              <div
                className="px-0 overflow-auto flex-grow-1 border-top"
                style={{
                  background: "#f8f9fa",
                  maxHeight: "calc(70vh - 200px)",
                }}
              >
                {/* Previous Notes Section confined to Notes tab */}

                {notes.length === 0 ? (
                  <p className="text-muted px-4">No notes found.</p>
                ) : (
                  notes.map((item) => (
                    <div key={item.id} className="mb-3 px-4 pt-3">
                      <div className="d-flex justify-content-between align-items-start mb-1">
                        <p className="mb-0 fw-semibold flex-grow-1">{item.note}</p>
                        <div className="d-flex align-items-center gap-2">
                          {item.displayOnStatement && (
                            <Badge bg="info">On Statement</Badge>
                          )}
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => handleToggleDisplayFlag(item)}
                            title={
                              item.displayOnStatement
                                ? "Remove from statement"
                                : "Add to statement"
                            }
                          >
                            <i
                              className={`fa-solid fa-${
                                item.displayOnStatement ? "minus" : "plus"
                              } me-1`}
                              style={{ fontSize: "0.75rem" }}
                            />
                            <i className="fa-solid fa-file-lines" />
                          </Button>
                        </div>
                      </div>
                      <div className="d-flex justify-content-end">
                        <small className="text-secondary text-sm">
                          {item.userName} • {formatDate(item.dateCreated)}
                        </small>
                      </div>
                      <hr className="my-2" />
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {activeTab === "history" && (
            <div className="border-top border-1 border-secondary mb-0">
              <Form.Label className="fw-bold mt-2">History Records:</Form.Label>
              <div
                className="px-0 overflow-auto flex-grow-1"
                style={{
                  background: "#f8f9fa",
                  maxHeight: "calc(70vh - 150px)",
                }}
              >
                {history.length === 0 ? (
                  <p className="text-muted px-4 mt-2">No history records found.</p>
                ) : (
                  history.map((item) => (
                    <div
                      key={item.id || item._id}
                      className="mb-2 px-4 pt-2"
                    >
                      <div className="d-flex justify-content-between align-items-start">
                        <p className="mb-1 flex-grow-1">{item.actionDescription}</p>
                        <small className="text-secondary text-sm ms-1 text-nowrap">
                          {item.userName} •{" "}
                          {new Date(item.dateCreated).toLocaleString()}
                        </small>
                      </div>
                      <hr className="my-2" />
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {error && <Alert variant="danger">{error}</Alert>}
        </Container>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export function NotesHistory({ account }) {
  const [showModal, setShowModal] = useState(false);
  return (
    <>
      <Button
        variant="primary"
        className="w-50 d-flex align-items-center justify-content-center gap-3"
        onClick={() => setShowModal(true)}
      >
        <i className="fa-regular fa-pen-to-square" /> Account Notes
      </Button>

      <NotesHistoryModal
        show={showModal}
        onHide={() => setShowModal(false)}
        accountId={account._id}
      />
    </>
  );
}